import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { urlFor } from "./sanity/client";
import { SanityImageSource } from "@sanity/image-url/lib/types/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Safely generates optimized image URLs from Sanity images with fallback support
 * Optimized for Google Discover and social media with proper dimensions and format
 * @param image - Sanity image object or string URL (can be null/undefined)
 * @param fallback - Fallback image URL (defaults to main OG image)
 * @param width - Optional width for image optimization (default: 1200 for OG/Discover)
 * @param height - Optional height for image optimization (default: 675 for 16:9 aspect)
 * @returns Safe, optimized image URL string (direct CDN URL, not Next.js proxy)
 */
export function getSafeImageUrl(
  image: SanityImageSource | string | null | undefined,
  fallback: string = "/images/og/OG.jpg",
  width: number = 1200,
  height: number = 675
): string {
  // Return fallback if no image
  if (!image) {
    return fallback;
  }

  // If it's already a string URL, return it (handles external URLs)
  if (typeof image === "string") {
    // If it's an HTTP URL, return as-is
    if (image.startsWith("http")) {
      return image;
    }
    // If it's a relative path, return as-is
    return image;
  }

  try {
    // Build optimized Sanity CDN URL
    let builder = urlFor(image)
      .width(width)
      .height(height)
      .fit("crop") // Ensures consistent aspect ratio by cropping
      .auto("format") // Automatically serves WebP/AVIF for modern browsers
      .quality(85); // Optimized quality for fast loading while maintaining visual quality

    return builder.url();
  } catch (error) {
    console.warn("Failed to generate image URL from Sanity:", error);
    return fallback;
  }
}

/**
 * Format a date to a readable string
 * @param date The date to format
 * @returns Formatted date string (e.g., "MON, JAN 01, 2026")
 */
// export function formatDate(date: Date, endDate: Date): string {
//   return date
//     .toLocaleDateString("en-US", {
//       weekday: "short",
//       month: "short",
//       day: "numeric",
//       year: "numeric",
//     })
//     .toUpperCase();
// }

export function formatDate(startDate: Date, endDate: Date): string {
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const sameMonth = startDate.getMonth() === endDate.getMonth();
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameDay = startDay === endDay && sameMonth && sameYear;

  const shortMonth = startDate.toLocaleString("en-US", { month: "short" });

  if (sameDay || (startDay === endDay - 1 && sameMonth && sameYear)) {
    // One-day event
    return `${startDay} ${shortMonth}, ${startDate.getFullYear()}`;
  }

  // For multi-day events, subtract 1 day from endDate to get the last inclusive day
  const inclusiveEndDate = new Date(endDate);
  inclusiveEndDate.setDate(inclusiveEndDate.getDate() - 1);
  
  const inclusiveEndDay = inclusiveEndDate.getDate();
  const inclusiveEndMonth = inclusiveEndDate.toLocaleString("en-US", { month: "short" });
  const inclusiveEndYear = inclusiveEndDate.getFullYear();

  if (sameMonth && sameYear) {
    return `${startDay}–${inclusiveEndDay} ${shortMonth}, ${startDate.getFullYear()}`;
  }

  // Check if inclusive end date is in the same month as start date (after adjustment)
  if (startDate.getMonth() === inclusiveEndDate.getMonth() && 
      startDate.getFullYear() === inclusiveEndYear) {
    return `${startDay}–${inclusiveEndDay} ${shortMonth}, ${startDate.getFullYear()}`;
  }

  return `${startDay} ${shortMonth} – ${inclusiveEndDay} ${inclusiveEndMonth}, ${startDate.getFullYear()}`;
}

/**
 * Format start and end time to a readable string
 * @param startDate The start date and time
 * @param endDate The end date and time
 * @returns Formatted time string (e.g., "8:00 PM - 11:00 PM")
 */
export function formatTime(startDate: Date, endDate: Date): string {
  const startTime = startDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const endTime = endDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${startTime} - ${endTime}`;
}

/**
 * Check if a date is in the past
 * @param date The date to check
 * @returns Boolean indicating if the date is in the past
 */
export function isPastEvent(date: Date): boolean {
  const now = new Date();
  return date < now;
}

export function formatDuration(durationMs: number): string {
  const minutes = Math.floor(durationMs / 60000);
  const seconds = Math.floor((durationMs % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Format an address for display
 * @param address The full address string
 * @param city The city
 * @param country The country
 * @returns Formatted address string
 */
export function formatAddress(
  address: string,
  city: string,
  country: string
): string {
  return [address, city, country].filter((part) => part?.trim()).join(", ");
}
